import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { getAuthToken } from "../utils/authToken";
import { useNavigate } from "react-router-dom";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";
import "./comments/CommentSection.css";
import DOMPurify from "dompurify";

// Set Quill globally so quill-emoji can auto-register itself
window.Quill = Quill;
require("quill-emoji");
require("quill-emoji/dist/quill-emoji.css");

const quillModules = {
  toolbar: {
    container: [
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'bullet' }, { 'list': 'ordered' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      ['code-block', 'blockquote'],
      ['link', 'image'],
      ['clean'],
      ['emoji']
    ],
    handlers: {
      link: function(value) {
        if (value) {
          const href = prompt('Enter the link URL:');
          if (href) {
            const range = this.quill.getSelection();
            if (range && range.length > 0) {
              this.quill.format('link', href);
            } else {
              const insertIndex = range ? range.index : this.quill.getLength();
              this.quill.insertText(insertIndex, href, 'link', href);
              this.quill.setSelection(insertIndex + href.length);
            }
          }
        } else {
          this.quill.format('link', false);
        }
      }
    }
  },
  "emoji-toolbar": true,
  "emoji-shortname": true,
  "emoji-textarea": false
};

const quillFormats = [
  'header', 'bold', 'italic', 'underline', 'strike', 'blockquote',
  'list', 'bullet', 'indent', 'link', 'image', 'code-block', 'emoji'
];


const POPULAR_EMOJIS = ["😊", "👍", "🔥", "❤️", "💡", "🙌", "👏", "🎉", "🤔", "📚", "🙏", "🎯"];

// Standalone avatar component — always shows colored initials; loads photo only if a real custom URL exists
function AvatarWithFallback({ pictureUrl, fallbackLetter, bgColor, size, fontSize, highlight }) {
  const [imgError, setImgError] = useState(false);
  // Only try to load if URL exists and is not a known default/placeholder
  const isRealPhoto = pictureUrl &&
    !pictureUrl.includes("default") &&
    !pictureUrl.includes("anonymous") &&
    !pictureUrl.includes("placeholder");
  const showImage = isRealPhoto && !imgError;
  return (
    <div style={{
      width: size,
      height: size,
      borderRadius: "50%",
      background: showImage ? "transparent" : bgColor,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "white",
      fontSize: fontSize,
      fontWeight: 700,
      overflow: "hidden",
      flexShrink: 0,
      border: highlight ? "2.5px solid var(--gold)" : "2px solid transparent",
      boxSizing: "border-box",
      boxShadow: highlight ? "0 0 0 2px rgba(184,134,11,0.3)" : "none",
      userSelect: "none",
      transition: "box-shadow 0.2s"
    }}>
      {showImage ? (
        <img
          src={pictureUrl}
          alt="Avatar"
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
          onError={() => setImgError(true)}
        />
      ) : (
        <span style={{ letterSpacing: 0, lineHeight: 1 }}>{fallbackLetter}</span>
      )}
    </div>
  );
}

export default function CommentSection({ bookId }) {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [sortOrder, setSortOrder] = useState("oldest"); // oldest, newest, popular

  // Top level comment form state
  const [content, setContent] = useState("");
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const textareaRef = useRef(null);

  // Active states for replies and reactions
  const [replyToId, setReplyToId] = useState(null); // comment id replying to
  const [replyContent, setReplyContent] = useState("");
  const [replyEmojiPickerId, setReplyEmojiPickerId] = useState(null);
  const replyTextareaRef = useRef(null);

  // Reaction picker state
  const [activeReactionPickerId, setActiveReactionPickerId] = useState(null);
  const [expandedComments, setExpandedComments] = useState({}); // Stores expanded/collapsed state for comments content (>280 chars)
  const [expandedThreads, setExpandedThreads] = useState({}); // Stores thread expand/collapse state (maps commentId -> bool)
  const [activeDropdownCommentId, setActiveDropdownCommentId] = useState(null); // Three-dot dropdown visibility

  useEffect(() => {
    fetchComments();
  }, [bookId]);

  const fetchComments = async () => {
    try {
      setLoading(true);
      const res = await fetch(`/api/comments?bookId=${bookId}`);
      if (res.ok) {
        const data = await res.json();
        setComments(data);
      }
    } catch (error) {
      console.error("Failed to fetch comments:", error);
    } finally {
      setLoading(false);
    }
  };

  // Safe client-side markdown formatting renderer
  const renderMarkdown = (text) => {
    if (!text) return "";

    // Detect if content is HTML from Quill (typically contains block tags)
    const isHtml = /<[a-z][\s\S]*>/i.test(text);
    if (isHtml) {
      return <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(text, { ADD_ATTR: ['target'] }) }} />;
    }

    // Escape HTML tags to prevent XSS, preserving <u> tags
    let html = text
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/&lt;u&gt;/g, "<u>")
      .replace(/&lt;\/u&gt;/g, "</u>");

    // Code Blocks: ```code```
    html = html.replace(/```([\s\S]*?)```/g, (match, code) => {
      return `<pre style="background: rgba(0,0,0,0.04); border: 1px solid var(--dust); padding: 10px; border-radius: 8px; font-family: monospace; font-size: 0.9rem; overflow-x: auto; margin: 8px 0; color: var(--ink);"><code>${code.trim()}</code></pre>`;
    });

    // Inline Code: `code`
    html = html.replace(/`([^`\n]+)`/g, "<code style='background: rgba(0,0,0,0.05); padding: 2px 6px; border-radius: 4px; font-family: monospace; font-size: 0.9rem; color: var(--ink);'>$1</code>");

    // Bold: **text**
    html = html.replace(/\*\*([\s\S]+?)\*\*/g, "<strong>$1</strong>");

    // Italic: *text*
    html = html.replace(/\*([\s\S]+?)\*/g, "<em>$1</em>");

    // Strikethrough: ~~text~~
    html = html.replace(/~~([\s\S]+?)~~/g, "<del>$1</del>");

    // Links: [text](url)
    html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, "<a href='$2' target='_blank' rel='noopener noreferrer' style='color: var(--gold); text-decoration: underline;'>$1</a>");

    // Tables: | col 1 | col 2 |
    if (html.includes("|")) {
      const lines = html.split("\n");
      let inTable = false;
      let tableHtml = "";
      let normalHtml = "";

      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        if (line.startsWith("|") && line.endsWith("|")) {
          if (!inTable) {
            inTable = true;
            tableHtml = "<div style='overflow-x: auto; margin: 10px 0;'><table style='border-collapse: collapse; width: 100%; border: 1px solid var(--dust); font-size: 0.9rem;'>";
          }
          const cells = line.split("|").slice(1, -1).map(c => c.trim());
          if (cells.every(c => /^:-*|-*:$/g.test(c) || /^-+$/g.test(c))) {
            continue; // separator
          }
          const isHeader = !tableHtml.includes("<tbody>") && !tableHtml.includes("<thead>");
          if (isHeader) {
            tableHtml += "<thead><tr style='background: rgba(0,0,0,0.03); border-bottom: 2px solid var(--dust);'>";
            cells.forEach(c => {
              tableHtml += `<th style='padding: 8px 12px; border: 1px solid var(--dust); text-align: left; font-weight: 600;'>${c}</th>`;
            });
            tableHtml += "</tr></thead><tbody>";
          } else {
            tableHtml += "<tr style='border-bottom: 1px solid var(--dust);'>";
            cells.forEach(c => {
              tableHtml += `<td style='padding: 8px 12px; border: 1px solid var(--dust);'>${c}</td>`;
            });
            tableHtml += "</tr>";
          }
        } else {
          if (inTable) {
            tableHtml += "</tbody></table></div>";
            normalHtml += tableHtml;
            inTable = false;
            tableHtml = "";
          }
          normalHtml += line + "<br/>";
        }
      }
      if (inTable) {
        tableHtml += "</tbody></table></div>";
        normalHtml += tableHtml;
      }
      html = normalHtml;
    } else {
      html = html.replace(/\n/g, "<br/>");
    }

    return <span dangerouslySetInnerHTML={{ __html: html }} />;
  };

  // Helper to format timestamps nicely
  const formatDate = (dateString) => {
    // SQLite returns 'YYYY-MM-DD HH:MM:SS' in UTC without 'Z'.
    // We must append 'Z' so the browser parses it as UTC, not local time.
    let parsedString = dateString;
    if (parsedString && !parsedString.includes('Z') && !parsedString.includes('T')) {
      parsedString = parsedString.replace(' ', 'T') + 'Z';
    }
    
    const d = new Date(parsedString);
    const now = new Date();
    const diff = (now - d) / 1000;

    if (diff < 60) return "Just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  };

  // Refactored toolbar formatting handler
  const handleToolbarAction = (action, isReply = false) => {
    const el = isReply ? replyTextareaRef.current : textareaRef.current;
    if (!el) return;

    const start = el.selectionStart;
    const end = el.selectionEnd;
    const text = isReply ? replyContent : content;
    const selected = text.slice(start, end);

    let replacement = "";
    let cursorOffset = 0;

    switch (action) {
      case "bold":
        replacement = `**${selected || "bold text"}**`;
        cursorOffset = selected ? 0 : 2;
        break;
      case "italic":
        replacement = `*${selected || "italic text"}*`;
        cursorOffset = selected ? 0 : 1;
        break;
      case "underline":
        replacement = `<u>${selected || "underlined text"}</u>`;
        cursorOffset = selected ? 0 : 3;
        break;
      case "strikethrough":
        replacement = `~~${selected || "strikethrough text"}~~`;
        cursorOffset = selected ? 0 : 2;
        break;
      case "code":
        replacement = `\`\`\`\n${selected || "code block"}\n\`\`\``;
        cursorOffset = selected ? 0 : 4;
        break;
      case "link":
        const url = prompt("Enter link URL:");
        if (!url) return;
        replacement = `[${selected || "link text"}](${url})`;
        break;
      case "table":
        replacement = `\n| Column 1 | Column 2 |\n|---|---|\n| Cell 1 | Cell 2 |\n`;
        break;
      case "bullet":
        replacement = selected ? selected.split("\n").map(l => `* ${l}`).join("\n") : "* item";
        break;
      case "number":
        replacement = selected ? selected.split("\n").map((l, i) => `${i + 1}. ${l}`).join("\n") : "1. item";
        break;
      case "indent":
        replacement = selected ? selected.split("\n").map(l => `  ${l}`).join("\n") : "  indented text";
        break;
      case "outdent":
        replacement = selected
          ? selected.split("\n").map(l => l.startsWith("  ") ? l.slice(2) : l.startsWith(" ") ? l.slice(1) : l).join("\n")
          : selected;
        break;
      default:
        return;
    }

    const newText = text.slice(0, start) + replacement + text.slice(end);

    if (isReply) {
      setReplyContent(newText);
    } else {
      setContent(newText);
    }

    setTimeout(() => {
      el.focus();
      const newCursorPos = start + replacement.length - cursorOffset;
      el.setSelectionRange(newCursorPos, newCursorPos);
    }, 50);
  };

  const handleInsertEmoji = (emoji, isReply = false) => {
    const el = isReply ? replyTextareaRef.current : textareaRef.current;
    if (!el) return;

    const start = el.selectionStart;
    const end = el.selectionEnd;
    const text = isReply ? replyContent : content;
    const newText = text.slice(0, start) + emoji + text.slice(end);

    if (isReply) {
      setReplyContent(newText);
      setReplyEmojiPickerId(null);
    } else {
      setContent(newText);
      setShowEmojiPicker(false);
    }

    setTimeout(() => {
      el.focus();
      const newCursorPos = start + emoji.length;
      el.setSelectionRange(newCursorPos, newCursorPos);
    }, 50);
  };

  // Posting comment (top-level or reply)
  const handlePost = async (e, parentId = null) => {
    e.preventDefault();
    if (!user) {
      navigate("/login");
      return;
    }

    const text = parentId ? replyContent.trim() : content.trim();
    if (!text) return;

    setPosting(true);
    try {
      const token = await getAuthToken();
      const res = await fetch("/api/comments", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          bookId,
          content: text,
          parentId
        })
      });

      if (res.ok) {
        if (parentId) {
          setReplyContent("");
          setReplyToId(null);
          // auto expand thread where reply was posted
          setExpandedThreads(prev => ({ ...prev, [parentId]: true }));
        } else {
          setContent("");
        }
        await fetchComments();
      }
    } catch (error) {
      console.error("Failed to post comment:", error);
    } finally {
      setPosting(false);
    }
  };

  // Toggle neutral Like reaction (value 4) or remove it
  const handleLikeToggle = async (commentId, currentReactions) => {
    if (!user) {
      navigate("/login");
      return;
    }

    const userReaction = currentReactions[user.uid];
    const newReaction = userReaction === "like" ? null : "like";

    await submitReaction(commentId, newReaction);
  };

  // Submits reaction details to backend
  const submitReaction = async (commentId, reactionType) => {
    // Optimistic local state update
    setComments(prev => prev.map(c => {
      if (c.id === commentId) {
        const nextReactions = { ...c.reactions };
        if (reactionType) {
          nextReactions[user.uid] = reactionType;
        } else {
          delete nextReactions[user.uid];
        }
        return { ...c, reactions: nextReactions };
      }
      return c;
    }));

    try {
      const token = await getAuthToken();
      const res = await fetch(`/api/comments/${commentId}/react`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({ reactionType })
      });
      if (!res.ok) {
        fetchComments(); // revert on failure
      }
    } catch (error) {
      console.error("Failed to submit reaction:", error);
      fetchComments(); // revert
    }
  };

  // Triggered when emoji is clicked in the popover
  const handleEmojiReact = (commentId, emoji) => {
    const currentReaction = comments.find(c => c.id === commentId)?.reactions?.[user.uid];
    const newReaction = currentReaction === emoji ? null : emoji;
    submitReaction(commentId, newReaction);
    setActiveReactionPickerId(null);
  };

  // Delete a comment
  const handleDelete = async (commentId) => {
    if (!window.confirm("Are you sure you want to delete this comment? All replies will also be deleted.")) return;

    // Optimistic delete
    setComments(prev => prev.filter(c => c.id !== commentId && c.parent_id !== commentId));
    setActiveDropdownCommentId(null);

    try {
      const token = await getAuthToken();
      const res = await fetch(`/api/comments/${commentId}`, {
        method: "DELETE",
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });
      if (!res.ok) {
        fetchComments();
      }
    } catch (error) {
      console.error("Failed to delete comment:", error);
      fetchComments();
    }
  };

  // Open reaction picker popup for a comment
  const openReactionPicker = (commentId) => {
    if (!user) {
      navigate("/login");
      return;
    }
    setActiveReactionPickerId(commentId);
  };

  // Format and group comments tree
  const processedComments = React.useMemo(() => {
    const parentComments = comments.filter(c => !c.parent_id);
    const getRepliesFor = (parentId) => comments.filter(c => c.parent_id === parentId);

    // Recursively calculate counts/reactions & reply mapping
    const buildTree = (commentsList) => {
      return commentsList.map(c => {
        const directReplies = getRepliesFor(c.id);

        // Sum reactions
        const reactionCount = Object.keys(c.reactions || {}).length;

        // Find top reaction types to render emojis
        const typeCounts = {};
        Object.values(c.reactions || {}).forEach(t => {
          typeCounts[t] = (typeCounts[t] || 0) + 1;
        });

        const topReactionEmojis = Object.keys(typeCounts)
          .sort((a, b) => typeCounts[b] - typeCounts[a])
          .slice(0, 3)
          .filter(Boolean);

        return {
          ...c,
          replies: buildTree(directReplies),
          reactionCount,
          topReactionEmojis
        };
      });
    };

    let tree = buildTree(parentComments);

    // Sort top level comments
    if (sortOrder === "newest") {
      tree.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    } else if (sortOrder === "popular") {
      tree.sort((a, b) => b.reactionCount - a.reactionCount);
    } else {
      tree.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    }

    return tree;
  }, [comments, sortOrder]);

  const totalCommentCount = comments.length;

  // Render initials or photo avatar — with graceful image error fallback
  const renderUserAvatar = (pictureUrl, nameOrEmail, size = "40px", fontSize = "14px", highlight = false) => {
    const fallbackLetter = (nameOrEmail || "U")[0].toUpperCase();
    // Generate a consistent warm color based on name
    const colors = ["#b8860b", "#8b5e3c", "#5b7fa6", "#7a6a9a", "#4a8c6f"];
    const colorIndex = (nameOrEmail || "U").charCodeAt(0) % colors.length;
    const bgColor = colors[colorIndex];
    return (
      <AvatarWithFallback
        pictureUrl={pictureUrl}
        fallbackLetter={fallbackLetter}
        bgColor={bgColor}
        size={size}
        fontSize={fontSize}
        highlight={highlight}
      />
    );
  };

  return (
    <div className="gc-comments-wrapper">
      

      {/* Header Row */}
      <div className="gc-header-row">
        <div className="gc-header-left">
          <h3 className="gc-header-title" style={{ display: "flex", alignItems: "baseline", gap: "8px" }}>
            Discussion
            <span style={{ fontSize: "1.1rem", color: "var(--muted)", fontWeight: 500, fontFamily: "'Outfit', sans-serif" }}>
              ({totalCommentCount})
            </span>
          </h3>
          <select
            className="gc-sort-dropdown"
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
          >
            <option value="oldest">Oldest</option>
            <option value="newest">Newest</option>
            <option value="popular">Top Comments</option>
          </select>
        </div>

        {/* User state indicator / Login links — only show for unauthenticated users */}
        <div className="gc-header-right">
          {!user && (
            <>
              <span className="gc-signup-link" onClick={() => navigate("/login")}>Sign up</span>
              <button className="gc-signin-btn" onClick={() => navigate("/login")}>Sign In</button>
            </>
          )}
        </div>
      </div>

      {/* Write a comment (top level composer) */}
      <div className="gc-composer">
        {user ? (
          renderUserAvatar(user.photoURL, user.displayName || user.email, "40px", "15px", true)
        ) : (
          <div style={{ width: "40px", height: "40px", borderRadius: "50%", background: "var(--dust)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.8rem", color: "var(--muted)", flexShrink: 0 }}>?</div>
        )}
        <div className="gc-composer-main">
          <form onSubmit={(e) => handlePost(e, null)}>
            <ReactQuill
              theme="snow"
              modules={quillModules}
              formats={quillFormats}
              value={content}
              onChange={setContent}
              placeholder={user ? "Start a conversation..." : "Sign in to join the conversation..."}
              readOnly={!user || posting}
              className="gc-composer-quill"
            />
            {user && (
              <div className="gc-composer-toolbar" style={{ borderTop: "none", justifyContent: "flex-end" }}>
                <button
                  type="submit"
                  className="gc-submit-btn"
                  disabled={posting || !content.replace(/<[^>]*>?/gm, '').trim()}
                >
                  {posting ? "Posting..." : "Post"}
                </button>
              </div>
            )}
          </form>
          {showEmojiPicker && (
            <div className="emoji-picker-popover">
              {POPULAR_EMOJIS.map(emoji => (
                <div
                  key={emoji}
                  className="emoji-item"
                  onClick={() => handleInsertEmoji(emoji, false)}
                >
                  {emoji}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Loading indicator */}
      {loading && (
        <div style={{ textAlign: "center", padding: "2rem 0", color: "var(--muted)", fontStyle: "italic" }}>
          Loading discussions...
        </div>
      )}

      {/* Empty State */}
      {!loading && processedComments.length === 0 && (
        <div style={{ textAlign: "center", padding: "3rem 1rem", color: "var(--muted)", border: "1px dashed var(--dust)", borderRadius: "12px", background: "rgba(0,0,0,0.01)" }}>
          <span style={{ fontSize: "2rem" }}>✍️</span>
          <p style={{ margin: "0.5rem 0 0", fontSize: "0.95rem" }}>No comments yet. Share your reflections and start the discussion!</p>
        </div>
      )}

      {/* Render Comments List */}
      {!loading && processedComments.map(comment => renderCommentNode(comment, false))}
    </div>
  );

  // Recursive comment node rendering helper
  function renderCommentNode(comment, isReply = false) {
    const userReaction = user ? comment.reactions[user.uid] : null;
    const isLiked = userReaction === "like";

    const isContentTruncated = comment.content.length > 280;
    const isExpanded = expandedComments[comment.id];
    const displayContent = (isContentTruncated && !isExpanded)
      ? comment.content.slice(0, 277) + "..."
      : comment.content;

    const hasReplies = comment.replies && comment.replies.length > 0;
    const isThreadExpanded = expandedThreads[comment.id];

    // Gather unique replier pictures or initials for the collapsed thread display
    const replierAvatars = [];
    if (hasReplies) {
      comment.replies.forEach(r => {
        if (replierAvatars.length < 3) {
          replierAvatars.push({
            name: r.user_name,
            picture: r.user_picture
          });
        }
      });
    }

    const isMenuOpen = activeDropdownCommentId === comment.id;

    return (
      <div
        key={comment.id}
        className={isReply ? "gc-reply-node" : "gc-comment-card"}
      >
        {/* Comment Header */}
        <div className="gc-card-header">
          <div className="gc-card-user-info">
            {renderUserAvatar(comment.user_picture, comment.user_name, isReply ? "32px" : "40px", isReply ? "11px" : "14px")}
            <div className="gc-user-text">
              <span className="gc-username" style={isReply ? { fontSize: "0.85rem" } : {}}>{comment.user_name}</span>
              <span className="gc-timestamp">{formatDate(comment.created_at)}</span>
            </div>
          </div>

          {/* Action Menu Trigger (Three dots ⋮) */}
          {user && (comment.user_id === user.uid) && (
            <div style={{ position: "relative" }}>
              <button
                className="gc-context-menu-trigger"
                onClick={() => setActiveDropdownCommentId(isMenuOpen ? null : comment.id)}
              >
                ⋮
              </button>
              {isMenuOpen && (
                <div className="gc-action-dropdown-menu">
                  <div
                    className="gc-dropdown-menu-item"
                    style={{ color: "#ef4444" }}
                    onClick={() => handleDelete(comment.id)}
                  >
                    🗑️ Delete
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Comment Content */}
        <div className="gc-card-body" style={isReply ? { fontSize: "0.88rem" } : {}}>
          {renderMarkdown(displayContent)}
          {isContentTruncated && (
            <button
              className="gc-readmore-btn"
              onClick={() => setExpandedComments(prev => ({ ...prev, [comment.id]: !isExpanded }))}
            >
              {isExpanded ? "Show less" : "Read more"}
            </button>
          )}
        </div>

        {/* Action Buttons & Reactions Row — matching reference layout */}
        <div className="gc-card-footer">

          {/* LEFT: Non-clickable emoji reaction pile */}
          <div
            className="gc-reactions-pile"
            title="Reactions to this comment"
            style={{ cursor: "default" }}
          >
            {comment.reactionCount > 0 ? (
              <>
                {comment.topReactionEmojis.map((emoji, i) => (
                  <span key={i} className="gc-emoji-badge" style={{ zIndex: 3 - i }}>{emoji}</span>
                ))}
                <span className="gc-reaction-count-num">{comment.reactionCount}</span>
              </>
            ) : (
              <span style={{ fontSize: "0.75rem", color: "var(--muted)", opacity: 0.5, padding: "0 4px" }}>—</span>
            )}
          </div>

          {/* RIGHT: React toggle + Reply */}
          <div className="gc-actions-group" style={{ position: "relative" }}>
            {(!user || user.uid !== comment.user_id) && (
              <button
                className={`gc-action-link-btn${userReaction ? " liked" : ""}`}
                onClick={() => openReactionPicker(comment.id)}
                title="React to this comment"
              >
                {userReaction ? (
                  <span style={{ fontSize: "1.1rem" }}>{userReaction}</span>
                ) : (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3H14z" />
                    <path d="M7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" />
                  </svg>
                )}
                {userReaction ? "Reacted" : "React"}
              </button>
            )}
            <button
              className="gc-action-link-btn"
              onClick={() => {
                setReplyToId(replyToId === comment.id ? null : comment.id);
                setReplyContent("");
              }}
              title="Reply to this comment"
            >
              {/* Reply icon */}
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
              Reply
            </button>
        {/* Inline Emoji Reaction Picker Popup */}
        {activeReactionPickerId === comment.id && (
          <div className="gc-emoji-popover" style={{
            position: "absolute",
            bottom: "2.5rem",
            right: "0",
            background: "var(--white)",
            border: "1px solid var(--dust)",
            borderRadius: "16px",
            padding: "0.85rem",
            boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
            zIndex: 120,
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: "8px",
            width: "max-content"
          }}>
            {/* Close button at top right */}
            <button className="gc-slider-close" style={{ position: "absolute", top: "4px", right: "8px", background: "none", border: "none", cursor: "pointer", color: "var(--muted)" }} onClick={() => setActiveReactionPickerId(null)}>✕</button>
            
            {POPULAR_EMOJIS.map(emoji => (
              <div
                key={emoji}
                className="emoji-item"
                style={{
                  fontSize: "1.5rem",
                  cursor: "pointer",
                  padding: "6px",
                  borderRadius: "8px",
                  textAlign: "center",
                  transition: "transform 0.15s, background 0.15s"
                }}
                onClick={() => handleEmojiReact(comment.id, emoji)}
                onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(0,0,0,0.05)"; e.currentTarget.style.transform = "scale(1.1)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.transform = "scale(1)"; }}
              >
                {emoji}
              </div>
            ))}
          </div>
        )}
          </div>
        </div>

        {/* Inline Reply Box Composer */}
        {replyToId === comment.id && (
          <div
            className="gc-composer gc-reply-composer"
            style={{
              marginTop: "0.75rem",
              border: "1px solid var(--dust)",
              boxShadow: "none",
              marginLeft: "0",
              borderRadius: "12px",
              padding: "0.85rem"
            }}
          >
            {/* Replying to label + close */}
            <div style={{
              position: "absolute",
              top: "8px",
              right: "8px",
              display: "flex",
              alignItems: "center",
              gap: "6px"
            }}>
              <span style={{
                fontSize: "0.72rem",
                color: "var(--muted)",
                fontStyle: "italic"
              }}>
                Replying to <strong style={{ color: "var(--gold)" }}>@{comment.user_name?.split(" ")[0] || "user"}</strong>
              </span>
              <button
                type="button"
                className="gc-slider-close"
                onClick={() => setReplyToId(null)}
              >
                ✕
              </button>
            </div>
            {user ? (
              renderUserAvatar(user.photoURL, user.displayName || user.email, "32px", "12px", true)
            ) : (
              <div style={{ width: "32px", height: "32px", borderRadius: "50%", background: "var(--dust)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.75rem", color: "var(--muted)" }}>?</div>
            )}
            <div className="gc-composer-main">
              <form onSubmit={(e) => handlePost(e, comment.id)}>
                <ReactQuill
                  theme="snow"
                  modules={quillModules}
                  formats={quillFormats}
                  value={replyContent}
                  onChange={setReplyContent}
                  placeholder={`Reply to ${comment.user_name?.split(" ")[0] || "this comment"}...`}
                  readOnly={posting}
                  className="gc-composer-quill reply-quill"
                />
                <div className="gc-composer-toolbar" style={{ borderTop: "none", justifyContent: "flex-end" }}>
                  <button
                    type="submit"
                    className="gc-submit-btn"
                    style={{ padding: "5px 16px", fontSize: "0.85rem", background: "var(--gold)", color: "white" }}
                    disabled={posting || !replyContent.replace(/<[^>]*>?/gm, '').trim()}
                  >
                    {posting ? "Sending..." : "↩ Reply"}
                  </button>
                </div>
              </form>
              {replyEmojiPickerId === comment.id && (
                <div className="emoji-picker-popover" style={{ bottom: "45px", left: "60px" }}>
                  {POPULAR_EMOJIS.map(emoji => (
                    <div
                      key={emoji}
                      className="emoji-item"
                      onClick={() => handleInsertEmoji(emoji, true)}
                    >
                      {emoji}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Reply-of-reply toggle (for nested reply nodes that have their own replies) */}
        {hasReplies && isReply && (
          <div style={{ marginTop: "0.6rem" }}>
            <button
              onClick={() => setExpandedThreads(prev => ({ ...prev, [comment.id]: !prev[comment.id] }))}
              className="gc-thread-toggle-btn"
              style={{ fontSize: "0.76rem", padding: "4px 8px" }}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                {replierAvatars.slice(0, 2).map((avatar, i) => (
                  <div key={i} style={{ marginLeft: i > 0 ? "-6px" : 0, border: "2px solid var(--card-bg)", borderRadius: "50%", zIndex: 2 - i }}>
                    {renderUserAvatar(avatar.picture, avatar.name, "18px", "8px")}
                  </div>
                ))}
              </div>
              <span style={{ marginLeft: "5px" }}>
                {comment.replies.length} {comment.replies.length === 1 ? "reply" : "replies"}
              </span>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                style={{ marginLeft: "auto", transition: "transform 0.2s", transform: isThreadExpanded ? "rotate(180deg)" : "rotate(0deg)" }}>
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
            {isThreadExpanded && (
              <div className="gc-replies-thread-container" style={{ marginTop: "0.5rem" }}>
                {comment.replies.map(reply => renderCommentNode(reply, true))}
              </div>
            )}
          </div>
        )}

        {/* Reply Thread Toggle */}
        {hasReplies && !isReply && (
          <div style={{ marginTop: "0.85rem", paddingTop: "0.75rem", borderTop: "1px solid var(--dust)" }}>
            <button
              onClick={() => setExpandedThreads(prev => ({ ...prev, [comment.id]: !prev[comment.id] }))}
              className="gc-thread-toggle-btn"
            >
              {/* Avatar stack */}
              <div style={{ display: "flex", alignItems: "center" }}>
                {replierAvatars.slice(0, 3).map((avatar, i) => (
                  <div key={i} style={{ marginLeft: i > 0 ? "-7px" : 0, border: "2px solid var(--card-bg)", borderRadius: "50%", zIndex: 3 - i }}>
                    {renderUserAvatar(avatar.picture, avatar.name, "22px", "9px")}
                  </div>
                ))}
              </div>
              <span style={{ marginLeft: "6px" }}>
                {comment.replies.length} {comment.replies.length === 1 ? "reply" : "replies"}
              </span>
              <svg
                width="13" height="13" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                style={{ marginLeft: "auto", transition: "transform 0.2s", transform: isThreadExpanded ? "rotate(180deg)" : "rotate(0deg)" }}
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
          </div>
        )}

        {/* Nested Thread Replies Container — with threading line */}
        {hasReplies && isThreadExpanded && !isReply && (
          <div className="gc-replies-thread-container">
            {comment.replies.map(reply => renderCommentNode(reply, true))}
          </div>
        )}
      </div>
    );
  }
}
